import { lazy, Suspense, useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppShell } from "@axisnode/shell";
import "./i18n";
import { LanguageSelector } from "./components/LanguageSelector";
import { QueryClientProvider } from "@tanstack/react-query";
import { createPlatformQueryClient } from "@axisnode/api-client";
import { API_BASE } from "./config";
import { ConnectivityProvider } from "./connectivity";
import { ScreenErrorBoundary } from "./components/ScreenErrorBoundary";
import { LoadingState } from "./components/ScreenStates";
import { ProjectSidebar } from "./components/ProjectSidebar";
import { emitObs } from "./obs";

const ProjectListScreen = lazy(() =>
  import("./screens/ProjectList").then((m) => ({ default: m.ProjectListScreen })),
);
const ProjectDetailScreen = lazy(() =>
  import("./screens/ProjectDetail").then((m) => ({
    default: m.ProjectDetailScreen,
  })),
);
const DocumentDetailScreen = lazy(() =>
  import("./screens/DocumentDetail").then((m) => ({
    default: m.DocumentDetailScreen,
  })),
);

const queryClient = createPlatformQueryClient();

function RouteObserver() {
  const location = useLocation();
  useEffect(() => {
    emitObs("navigation", "route_change", { pathname: location.pathname });
  }, [location.pathname, location.search]);
  return null;
}

export function App() {
  const { t } = useTranslation();
  return (
    <QueryClientProvider client={queryClient}>
      <ConnectivityProvider>
        <AppShell
          apiBaseUrl={API_BASE}
          title={t("app.title")}
          requireAuth
          navItems={[{ id: "projects", label: t("app.navProjects"), href: "/projects" }]}
          headerExtra={<LanguageSelector />}
        >
          <RouteObserver />
          <div className="ax-app-layout">
            <ProjectSidebar />
            <div className="ax-app-content">
              <Suspense fallback={<LoadingState />}>
                <Routes>
                  <Route path="/" element={<Navigate to="/projects" replace />} />
                  <Route
                    path="/projects"
                    element={
                      <ScreenErrorBoundary screen="ProjectList">
                        <ProjectListScreen />
                      </ScreenErrorBoundary>
                    }
                  />
                  <Route
                    path="/projects/:projectId"
                    element={
                      <ScreenErrorBoundary screen="ProjectDetail">
                        <ProjectDetailScreen />
                      </ScreenErrorBoundary>
                    }
                  />
                  <Route
                    path="/projects/:projectId/documents/:documentId"
                    element={
                      <ScreenErrorBoundary screen="DocumentDetail">
                        <DocumentDetailScreen />
                      </ScreenErrorBoundary>
                    }
                  />
                  <Route path="*" element={<Navigate to="/projects" replace />} />
                </Routes>
              </Suspense>
            </div>
          </div>
        </AppShell>
      </ConnectivityProvider>
    </QueryClientProvider>
  );
}
